<template>
	<v-container fluid class="pa-0 whatsapp-chat-container round-card elevation-1 mt-2">
		<v-row no-gutters class="fill-height">
			<!-- Conversations List -->
			<v-col cols="12" md="4" lg="3" class="conversations-sidebar">
				<v-card flat class="fill-height d-flex flex-column" style="border-top-left-radius: 15px !important;">
					<!-- Header -->
					<v-card-title style="padding: 26px">
						<v-icon left>mdi-whatsapp</v-icon>
						Chat WhatsApp
						<v-spacer></v-spacer>
						<v-badge
							v-if="totalUnread > 0"
							color="red"
							:content="totalUnread"
							overlap>
							<v-icon>mdi-message-badge</v-icon>
						</v-badge>
					</v-card-title>
					<v-divider></v-divider>
					<!-- Search -->
					<v-card-text class="pb-0 pt-4">
						<v-text-field
							v-model="searchQuery"
							append-icon="mdi-magnify"
							label="Cerca conversazione..."
							single-line
							hide-details
							rounded
							clearable
							dense
							outlined
							></v-text-field>
					</v-card-text>

					<!-- Filters -->
					<v-card-text class="py-2 px-0">
						<v-chip-group
							v-model="statusFilter"
							active-class="primary--text"
						>
							<v-chip small value="all">Tutte</v-chip>
							<v-chip small value="unread">Da leggere</v-chip>
							<v-chip small value="active">Attive</v-chip>
							<!-- <v-chip small value="pending">In attesa</v-chip> -->
							<v-chip small value="closed">Chiuse</v-chip>
						</v-chip-group>
					</v-card-text>

					<!-- Conversations List -->
					<v-divider></v-divider>
					<!-- Loading indicator -->
					<v-progress-linear
						v-if="conversationLoading"
						indeterminate
						color="primary"
						height="2"
					></v-progress-linear>
					<v-list
						ref="conversationsList"
						two-line
						class="conversations-list flex-grow-1 overflow-y-auto"
					>
						<div
							v-for="conversation in conversations"
							:key="conversation.id"
							class="conversation-item-wrapper"
						>
							<v-list-item
								@click="selectConversation(conversation)"
								:class="{
									'v-list-item--active': currentConversation && currentConversation.id === conversation.id
								}"
								class="conversation-item"
							>
								<v-list-item-avatar>
									<v-avatar
										:color="conversation.contact_name ? 'primary' : 'grey lighten-1'"
										size="40"
									>
										<span v-if="conversation.contact_name" class="white--text">
											{{ getInitials(conversation.contact_name) }}
										</span>
										<v-icon v-else color="white">mdi-account-question</v-icon>
									</v-avatar>
								</v-list-item-avatar>

								<v-list-item-content>
									<v-list-item-title class="d-flex align-center">
										<span class="text-truncate flex-grow-0" style="max-width: calc(100% - 40px);">
											{{ conversation.contact_name || conversation.telephone_number }}
										</span>
										<span class="flex-shrink-0 ml-2" style="min-width: 24px;">
											<v-chip
												v-if="conversation.unread_count > 0 || conversation.unread_count === -1"
												color="red"
												text-color="white"
												x-small
												:class="conversation.unread_count === -1 ? 'unread-dot' : ''"
											>
												{{ conversation.unread_count === -1 ? '' : conversation.unread_count }}
											</v-chip>
										</span>
									</v-list-item-title>
									<v-list-item-subtitle>
										{{ getLastMessageText(conversation) | truncate(30) }}
									</v-list-item-subtitle>
								</v-list-item-content>

								<v-list-item-action>
									<v-list-item-action-text>
										{{ formatDate(conversation.last_message_at) }}
									</v-list-item-action-text>
									<v-chip
										:color="getStatusColor(conversation.status)"
										x-small
										dark
									>
										{{ getStatusLabel(conversation.status) }}
									</v-chip>
								</v-list-item-action>
							</v-list-item>
							<v-divider></v-divider>
						</div>
						<!-- Loading indicator for more conversations -->
						<div v-if="loadingMoreConversations" class="text-center py-4">
							<v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
						</div>
					</v-list>
					<v-divider></v-divider>
					<!-- New Conversation Button -->
					<v-card-actions style="padding: 18px">
						<v-btn
							color="primary"
							block
							large
							@click="newConversationDialog = true"
						>
							<v-icon left>mdi-plus</v-icon>
							Nuova Conversazione
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-col>

			<!-- Chat Area -->
			<v-col cols="12" :md="conversationInfo ? 5 : 8" :lg="conversationInfo ? 6 : 9" class="chat-area">
				<v-card flat class="fill-height d-flex flex-column" :style="conversationInfo ? '' : 'border-top-right-radius: 15px !important;'">
					<!-- No conversation selected -->
					<v-container
						v-if="!currentConversation"
						class="d-flex align-center justify-center fill-height"
					>
						<div class="text-center">
							<v-icon size="100" color="grey lighten-1">mdi-whatsapp</v-icon>
							<h2 class="grey--text text--lighten-1 mt-4">
								Seleziona una conversazione
							</h2>
							<p class="grey--text">
								Scegli una chat dalla lista o inizia una nuova conversazione
							</p>
						</div>
					</v-container>

					<!-- Conversation selected -->
					<template v-else>
						<!-- Chat Header -->
						<v-card-title class="chat-header">
							<v-avatar
								size="40"
								:color="currentConversation.contact_name ? 'primary' : 'grey lighten-1'"
								class="mr-3"
							>
								<span v-if="currentConversation.contact_name" class="white--text fs16">
									{{ getInitials(currentConversation.contact_name) }}
								</span>
								<v-icon v-else>mdi-account-question</v-icon>
							</v-avatar>
							<div>
								<div class="font-weight-bold">
									{{ currentConversation.contact_name || currentConversation.telephone_number }}
								</div>
								<div class="caption">
									<v-icon x-small>mdi-phone</v-icon>
									{{ currentConversation.telephone_number }}
									<v-chip
										v-if="isCurrentConversationVerified"
										color="success"
										x-small
										class="ml-2"
									>
										<v-icon x-small left>mdi-check-decagram</v-icon>
										Verificato
									</v-chip>
									<template v-if="currentConversation.company">
										| <v-icon x-small>mdi-domain</v-icon>
										{{ currentConversation.company.name }}
									</template>
								</div>
							</div>
							<v-spacer></v-spacer>
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										icon
										v-bind="attrs"
										v-on="on"
										@click="conversationInfo = true"
									>
										<v-icon>mdi-information-outline</v-icon>
									</v-btn>
								</template>
								<span>Informazioni conversazione</span>
							</v-tooltip>
							<v-menu bottom left offset-y content-class="round-card" z-index="1000">
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										icon
										v-bind="attrs"
										v-on="on"
									>
										<v-icon>mdi-dots-vertical</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item @click="refreshMessages" :disabled="isRefreshing">
										<v-list-item-icon class="mr-2">
											<v-icon :class="{ 'mdi-spin': isRefreshing }">mdi-refresh</v-icon>
										</v-list-item-icon>
										<v-list-item-title class="pl-2">Aggiorna messaggi</v-list-item-title>
									</v-list-item>

									<v-list-item @click="markConversationAsUnread">
										<v-list-item-icon class="mr-2">
											<v-icon>mdi-message-badge-outline</v-icon>
										</v-list-item-icon>
										<v-list-item-title class="pl-2">Segna come da leggere</v-list-item-title>
									</v-list-item>

									<v-list-item @click="syncDialog = true">
										<v-list-item-icon class="mr-2">
											<v-icon>mdi-sync</v-icon>
										</v-list-item-icon>
										<v-list-item-title class="pl-2">Sincronizza messaggi</v-list-item-title>
									</v-list-item>

									<v-list-item @click="mediaGalleryDialog = true">
										<v-list-item-icon class="mr-2">
											<v-icon>mdi-image-multiple</v-icon>
										</v-list-item-icon>
										<v-list-item-title class="pl-2">Galleria media</v-list-item-title>
									</v-list-item>

									<v-list-item @click="linkingDialog = true">
										<v-list-item-icon class="mr-2">
											<v-icon color="orange">mdi-link-variant</v-icon>
										</v-list-item-icon>
										<v-list-item-title class="pl-2">Collega a contatto/azienda</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</v-card-title>

						<v-divider></v-divider>

						<!-- Messages Area -->
						<v-card-text
							ref="messagesContainer"
							class="messages-container flex-grow-1 overflow-y-auto"
							@scroll.passive="onScroll"
						>
							<v-container>
								<!-- Loading indicator for older messages -->
								<div v-if="loadingOlderMessages" class="text-center py-2">
									<v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
								</div>
								<div
									v-for="(group, date) in groupedMessages"
									:key="date"
									class="message-date-group"
								>
									<!-- Date Separator -->
									<div class="date-separator my-4">
										<v-chip
											small
											class="date-chip elevation-0"
										>
											{{ formatMessageDate(date) }}
										</v-chip>
									</div>

									<!-- Messages -->
									<div
										v-for="message in group"
										:key="message.id"
										:ref="`message-${message.id}`"
										class="message-wrapper mb-3"
										:class="message.direction === 'outbound' ? 'text-right' : 'text-left'"
									>
										<v-card
											:color="message.direction === 'outbound' ? '#DCF8C6' : 'white'"
											class="message-bubble d-inline-block elevation-1 message-bubble-with-menu"
											:class="[
												message.direction === 'outbound' ? 'ml-auto message-sent' : 'mr-auto message-received',
												highlightedMessageId === message.id ? 'message-highlighted' : ''
											]"
											max-width="70%"
											@mouseenter="hoveredMessageId = message.id"
											@mouseleave="onMessageMouseLeave(message.id)"
										>
											<!-- Message menu (visible on hover) -->
											<v-menu
												v-if="hoveredMessageId === message.id"
												v-model="openMenuId[message.id]"
												bottom
												left
            									content-class="round-card"
												offset-y
												:close-on-content-click="true"
											>
												<template v-slot:activator="{ on }">
													<v-btn
														icon
														x-small
														class="message-menu-btn"
														v-on="on"
														@click.stop
													>
														<v-icon small>mdi-chevron-down</v-icon>
													</v-btn>
												</template>

												<v-list dense>
													<v-list-item @click="copyMessage(message)">
														<v-list-item-icon class="mr-2">
															<v-icon small>mdi-content-copy</v-icon>
														</v-list-item-icon>
														<v-list-item-title>Copia</v-list-item-title>
													</v-list-item>

													<v-list-item @click="showMessageInfo(message)">
														<v-list-item-icon class="mr-2">
															<v-icon small>mdi-information-outline</v-icon>
														</v-list-item-icon>
														<v-list-item-title>Info</v-list-item-title>
													</v-list-item>
												</v-list>
											</v-menu>
											<v-card-text class="pa-3 pb-1">
												<!-- Reply context indicator -->
												<div
													v-if="message.replied_to_message"
													class="reply-context mb-2"
													@click="scrollToMessage(message.replied_to_message.id)"
												>
													<div class="reply-bar"></div>
													<div class="reply-content">
														<div class="reply-author">
															{{ message.replied_to_message.direction === 'outbound' ? 'Tu' : (currentConversation.contact_name || currentConversation.telephone_number) }}
														</div>
														<div class="reply-text">{{ truncateText(message.replied_to_message.body, 50) }}</div>
													</div>
												</div>

												<!-- Template indicator -->
												<v-chip
													v-if="message.message_type === 'template'"
													x-small
													color="info"
													class="mb-1"
												>
													<v-icon x-small left>mdi-file-document</v-icon>
													Template
												</v-chip>

												<!-- Message body -->
												<div v-if="!message.media_type || (message.media_type !== 'vcard' && message.media_type !== 'location')" class="message-body" v-html="formatMessageBody(message.body)"></div>

												<!-- vCard -->
												<VCardMessage
													v-if="message.media_type === 'vcard'"
													:message="message"
												/>

												<!-- Location -->
												<LocationMessage
													v-if="message.media_type === 'location'"
													:message="message"
												/>

												<!-- Media -->
												<div v-if="message.media_urls && message.media_urls.length && message.media_type !== 'vcard' && message.media_type !== 'location'" class="mt-0">
													<template v-for="(url, index) in message.media_urls">
														<!-- Image -->
														<v-img
															v-if="isImageMedia(message, index)"
															:key="`img-${index}`"
															:src="getMediaSrc(url, message, index)"
															class="media-image mb-2 cursor-pointer"
															@click="openMedia(getMediaSrc(url, message, index), getMediaType(message, index), message, index)"
															@load="onMediaLoaded"
															@error="(e) => handleMediaError(e, url, message, index)"
															aspect-ratio="1"
														></v-img>

														<!-- Audio -->
														<div
															v-else-if="isAudioMedia(message, index)"
															:key="`audio-${index}`"
															class="audio-wrapper mb-1"
														>
															<div class="audio-icon">
																<v-icon color="green darken-2">mdi-microphone</v-icon>
															</div>
															<audio
																controls
																:src="getMediaUrl(url)"
																class="audio-player"
															></audio>
														</div>

														<!-- Video -->
														<video
															v-else-if="isVideoMedia(message, index)"
															:key="`video-${index}`"
															controls
															:src="getMediaUrl(url)"
															class="media-video mb-2"
															@loadedmetadata="onMediaLoaded"
														></video>

														<!-- Document/Unknown -->
														<div
															v-else
															:key="`doc-${index}`"
															class="mb-2"
														>
															<v-btn
																small
																depressed
																class="document-button"
																@click="openMedia(getMediaSrc(url, message, index), getMediaType(message, index), message, index)"
															>
																<v-icon left>mdi-file-document-outline</v-icon>
																File allegato
															</v-btn>
														</div>
													</template>
												</div>

												<!-- Message info -->
												<div class="message-info text-right mt-n1">
													<span class="message-time mr-1">{{ formatTime(message.sent_at || message.created_at) }}</span>
													<v-icon
														v-if="message.direction === 'outbound'"
														small
														:color="getMessageStatusColor(message.status)"
													>
														{{ getMessageStatusIcon(message.status) }}
													</v-icon>
												</div>
											</v-card-text>
										</v-card>
									</div>
								</div>

								<!-- Typing indicator -->
								<div v-if="isTyping" class="text-left mb-3">
									<v-chip small color="grey lighten-3">
										<v-progress-circular
											indeterminate
											size="15"
											width="2"
											class="mr-2"
										></v-progress-circular>
										Sta scrivendo...
									</v-chip>
								</div>
							</v-container>
						</v-card-text>

						<!-- Scroll to bottom button -->
                		<v-fab-transition>
							<v-btn
								v-show="showScrollDownButton"
								fab
								small
								color="primary"
								:class="['scroll-down-btn', { 'scroll-down-btn--with-info': conversationInfo }]"
								elevation="4"
								@click="scrollToBottom"
							>
								<v-icon color="white">mdi-chevron-down</v-icon>
							</v-btn>
						</v-fab-transition>

						<!-- Message Input -->
						<v-divider></v-divider>
						<v-card-actions class="pa-3">
							<v-text-field
								ref="messageInput"
								v-model="newMessage"
								:disabled="!canSendMessage"
								placeholder="Scrivi un messaggio..."
								outlined
								hide-details
								rounded
								append-icon="mdi-send"
								@keyup.enter="sendMessage"
								@click:append="sendMessage"
							>
								<template v-slot:prepend>
									<v-tooltip bottom>
										<template v-slot:activator="{ on, attrs }">
											<v-btn
												icon
												@click="openUploadDialog"
												class="mx-1"
												style="align-self: center"
												v-bind="attrs"
												v-on="on"
											>
												<v-icon>mdi-paperclip</v-icon>
											</v-btn>
										</template>
										<span>Allega file</span>
									</v-tooltip>
									<v-tooltip bottom>
										<template v-slot:activator="{ on, attrs }">
											<v-btn
												icon
												@click="templateDialog = true"
												class="mx-1"
												style="align-self: center"
												v-bind="attrs"
												v-on="on"
											>
												<v-icon>mdi-file-document</v-icon>
											</v-btn>
										</template>
										<span>Invia template</span>
									</v-tooltip>
									<v-menu
										v-model="emojiMenu"
										:close-on-content-click="false"
										offset-y
										top
            							content-class="round-card"
										max-width="350"
										:z-index="9999"
									>
										<template v-slot:activator="{ on, attrs }">
											<v-tooltip bottom>
												<template v-slot:activator="{ on: tooltip }">
													<v-btn
														icon
														class="mx-1"
														style="align-self: center"
														v-bind="attrs"
														v-on="{ ...on, ...tooltip }"
													>
														<v-icon>mdi-emoticon-happy-outline</v-icon>
													</v-btn>
												</template>
												<span>Aggiungi emoji</span>
											</v-tooltip>
										</template>
										<v-card class="round-card emoji-picker-card" @click.stop>
											<VEmojiPicker
												@select="onSelectEmoji"
												:dark="$vuetify.theme.dark"
												:i18n="emojiPickerI18n"
											/>
											<v-divider></v-divider>
											<div class="pa-2 text-caption text--secondary text-center emoji-help-text">
												💡 Cerca in inglese (es: "smile", "heart", "fire")
											</div>
										</v-card>
									</v-menu>
								</template>
							</v-text-field>
						</v-card-actions>

						<!-- Template notice -->
						 <template v-if="!canSendMessage">
							<v-divider></v-divider>
							<v-alert
								type="warning"
								dense
								text
								class="ma-0 text-center">
								<v-icon small left>mdi-info</v-icon>
								La finestra di conversazione è scaduta. Usa un template per inviare un nuovo messaggio.
							</v-alert>
						</template>
					</template>
				</v-card>
			</v-col>

			<!-- Conversation Info Panel -->
			<v-col v-if="conversationInfo" cols="12" md="3" lg="3" class="pa-0 fill-height">
				<ConversationInfo
					v-model="conversationInfo"
					:conversation="currentConversation"
					@conversation-updated="onConversationUpdated"
				/>
			</v-col>
		</v-row>

		<!-- Dialogs -->
		<NewConversationDialog
			v-model="newConversationDialog"
			@created="onConversationCreated"
		/>

		<TemplateSelector
			v-model="templateDialog"
			:conversation-id="currentConversation ? currentConversation.id : null"
			@template-selected="onTemplateSelected"
		/>

		<MediaViewer
			v-model="mediaDialog"
			:src="currentMediaUrl"
			:type="currentMediaType"
			:message="currentMediaMessage"
			:mediaIndex="currentMediaIndex"
		/>

		<MediaGallery
			v-if="currentConversation"
			v-model="mediaGalleryDialog"
			:conversation-id="currentConversation.id"
		/>

		<!-- Linking Dialog -->
		<whatsapp-linking-dialog
			v-if="currentConversation"
			v-model="linkingDialog"
			:conversation="currentConversation"
			@linked="onConversationLinked"
		/>

		<!-- Sync Messages Dialog -->
		<v-dialog v-model="syncDialog" max-width="500">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3 background-1">
					<v-icon left color="primary">mdi-sync</v-icon>
					<p class="pl-2">Sincronizza Messaggi WhatsApp</p>
				</v-card-title>

				<v-divider></v-divider>

				<v-card-text class="pa-6">
					<div class="text-body-1 mb-4">
						Vuoi sincronizzare tutti i messaggi di questa conversazione dal Provider?
					</div>
					<div class="text-caption text--secondary mb-4">
						I messaggi mancanti saranno scaricati e aggiunti al database. Lo status dei messaggi esistenti sarà aggiornato.
					</div>

					<gmh-date-picker
						v-model="syncFromDate"
						label="Sincronizza da data (opzionale)"
						prepend-icon="mdi-calendar"
						:append-icon="null"
						:max="maxSyncDate"
						:clear-value="null"
						hint="Se non specificato, verranno sincronizzati gli ultimi 30 giorni"
						persistent-hint
					></gmh-date-picker>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions class="pa-3">
					<v-spacer></v-spacer>
					<v-btn text @click="syncDialog = false" :disabled="syncLoading">
						Annulla
					</v-btn>
					<v-btn
						color="primary"
						@click="syncMessages"
						:loading="syncLoading"
					>
						<v-icon left>mdi-sync</v-icon>
						Sincronizza
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Upload Media Dialog -->
		<v-dialog v-model="uploadDialog" max-width="600" min-height="400">
			<v-card class="background-2 round-card">
            	<v-card-title class="pa-3 background-1">
					<v-icon left>mdi-paperclip</v-icon>
					<p class="pl-2 pl-xl-3">Invia File</p>
				</v-card-title>

				<v-divider></v-divider>

				<v-card-text class="pa-6">
					<input
						ref="fileInput"
						type="file"
						style="display: none"
						accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
						@change="handleFileSelect"
					/>

					<v-btn
						v-if="!selectedFile"
						block
						large
						outlined
						color="primary"
						@click="$refs.fileInput.click()"
					>
						<v-icon left>mdi-file-upload</v-icon>
						Seleziona File
					</v-btn>

					<div v-if="selectedFile" class="mt-4">
						<!-- Image Preview -->
						<div v-if="filePreview" class="text-center mb-4">
							<v-img
								:src="filePreview"
								max-width="400"
								max-height="300"
								contain
								class="mx-auto"
							></v-img>
						</div>

						<!-- File Info -->
						<v-card outlined class="rounded-lg">
							<v-list>
								<v-list-item>
									<v-list-item-avatar>
										<v-icon large color="primary">
											{{ filePreview ? 'mdi-image' : 'mdi-file' }}
										</v-icon>
									</v-list-item-avatar>
									<v-list-item-content>
										<v-list-item-title>{{ selectedFile.name }}</v-list-item-title>
										<v-list-item-subtitle>
											{{ (selectedFile.size / 1024 / 1024).toFixed(2) }} MB
										</v-list-item-subtitle>
									</v-list-item-content>
									<v-list-item-action>
										<v-btn icon @click="selectedFile = null; filePreview = null; $refs.fileInput.value = ''">
											<v-icon>mdi-close</v-icon>
										</v-btn>
									</v-list-item-action>
								</v-list-item>
							</v-list>
						</v-card>

						<!-- Caption -->
						<v-textarea
							v-model="caption"
							label="Didascalia (opzionale)"
							outlined
							rows="3"
							class="mt-4"
							placeholder="Aggiungi una didascalia..."
						></v-textarea>
					</div>
				</v-card-text>
	
				<v-divider></v-divider>

				<v-card-actions class="pa-3">
					<v-spacer></v-spacer>
					<v-btn text @click="cancelUpload">
						Annulla
					</v-btn>
					<v-btn
						color="primary"
						:disabled="!selectedFile"
						:loading="uploading"
						@click="sendMediaFile"
					>
						<v-icon left>mdi-send</v-icon>
						Invia
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Message Info Dialog -->
		<v-dialog v-model="messageInfoDialog" max-width="500">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3 background-1">
					<v-icon left>mdi-information-outline</v-icon>
					<p class="pl-2">Info Messaggio</p>
				</v-card-title>

				<v-divider></v-divider>

				<v-card-text class="pa-4" v-if="selectedMessage">
					<v-list>
						<v-list-item>
							<v-list-item-content>
								<v-list-item-subtitle>Direzione</v-list-item-subtitle>
								<v-list-item-title>
									<v-chip small :color="selectedMessage.direction === 'outbound' ? 'primary' : 'secondary'">
										{{ selectedMessage.direction === 'outbound' ? 'Inviato' : 'Ricevuto' }}
									</v-chip>
								</v-list-item-title>
							</v-list-item-content>
						</v-list-item>

						<v-list-item v-if="selectedMessage.sent_at">
							<v-list-item-content>
								<v-list-item-subtitle>Inviato</v-list-item-subtitle>
								<v-list-item-title>{{ formatDateTime(selectedMessage.sent_at) }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>

						<v-list-item v-if="selectedMessage.delivered_at">
							<v-list-item-content>
								<v-list-item-subtitle>Consegnato</v-list-item-subtitle>
								<v-list-item-title>{{ formatDateTime(selectedMessage.delivered_at) }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>

						<v-list-item v-if="selectedMessage.read_at">
							<v-list-item-content>
								<v-list-item-subtitle>Letto</v-list-item-subtitle>
								<v-list-item-title>{{ formatDateTime(selectedMessage.read_at) }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>

						<v-list-item>
							<v-list-item-content>
								<v-list-item-subtitle>Stato</v-list-item-subtitle>
								<v-list-item-title>
									<v-chip small :color="getMessageStatusColor(selectedMessage.status)">
										<v-icon left small>{{ getMessageStatusIcon(selectedMessage.status) }}</v-icon>
										{{ selectedMessage.status }}
									</v-chip>
								</v-list-item-title>
							</v-list-item-content>
						</v-list-item>

						<v-list-item v-if="selectedMessage.error_message">
							<v-list-item-content>
								<v-list-item-subtitle>Errore</v-list-item-subtitle>
								<v-list-item-title class="error--text">
									{{ selectedMessage.error_message }}
								</v-list-item-title>
							</v-list-item-content>
						</v-list-item>

						<v-list-item v-if="selectedMessage.provider_message_id">
							<v-list-item-content>
								<v-list-item-subtitle>Message SID</v-list-item-subtitle>
								<v-list-item-title class="text-caption">
									{{ selectedMessage.provider_message_id }}
								</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</v-list>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions class="pa-2 background-1">
					<v-spacer></v-spacer>
					<v-btn text @click="messageInfoDialog = false">
						Chiudi
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import { VEmojiPicker } from 'v-emoji-picker'
import MediaViewer from '@/components/modules/whatsapp/MediaViewer'
import VCardMessage from '@/components/modules/whatsapp/VCardMessage'
import MediaGallery from '@/components/modules/whatsapp/MediaGallery'
import LocationMessage from '@/components/modules/whatsapp/LocationMessage'
import TemplateSelector from '@/components/modules/whatsapp/TemplateSelector'
import ConversationInfo from '@/components/modules/whatsapp/ConversationInfo'
import WhatsappLinkingDialog from '@/components/modules/whatsapp/WhatsAppLinkingDialog'
import NewConversationDialog from '@/components/modules/whatsapp/NewConversationDialog'
import { escapeAndLinkify } from '@/utils/sanitizeHtml'

export default {
	name: 'WhatsAppChat',

	components: {
		MediaViewer,
		MediaGallery,
		VEmojiPicker,
		VCardMessage,
		LocationMessage,
		ConversationInfo,
		TemplateSelector,
		WhatsappLinkingDialog,
		NewConversationDialog
	},

	data: () => ({
		// === CONVERSATIONS STATE (migrato da store) ===
		conversations: [],
		currentConversation: null,
		conversationMessages: [],
		conversationLoading: false,
		loadingMoreConversations: false,
		conversationsPage: 1,
		hasMoreConversations: false,
		hasMoreMessages: false,
		loadingOlderMessages: false,
		totalUnreadCount: 0,

		// === UI STATE ===
		searchQuery: '',
		searchDebounceTimer: null,
		statusFilter: 'all',
		newMessage: '',
		isTyping: false,
		newConversationDialog: false,
		templateDialog: false,
		conversationInfo: false,
		attachmentMenu: false,
		mediaDialog: false,
		currentMediaUrl: null,
		currentMediaType: 'image',
		currentMediaMessage: null,
		currentMediaIndex: null,
		typingTimer: null,
		isRefreshing: false,
		uploadDialog: false,
		selectedFile: null,
		filePreview: null,
		caption: '',
		uploading: false,
		mediaGalleryDialog: false,
		linkingDialog: false,

		// === EMOJI STATE ===
		emojiMenu: false,
		emojiPickerI18n: {
			search: 'Cerca...',
			categories: {
				Activity: 'Attività',
				Flags: 'Bandiere',
				Foods: 'Cibi e bevande',
				Frequent: 'Usati di recente',
				Objects: 'Oggetti',
				Nature: 'Animali e natura',
				Peoples: 'Faccine ed emozioni',
				Symbols: 'Simboli',
				Places: 'Viaggi e luoghi'
			}
		},

		// === SYNC STATE ===
		syncDialog: false,
		syncLoading: false,
		syncFromDate: null,

		// === MESSAGE ACTIONS STATE ===
		hoveredMessageId: null,
		openMenuId: {},
		messageInfoDialog: false,
		selectedMessage: null,
		highlightedMessageId: null,

		// === SCROLL STATE ===
		isAtBottom: true,
		scrollBottomThreshold: 100,
		showScrollDownButton: false
	}),

	computed: {
		// non si puo' sincronizzare da una data futura
		maxSyncDate() {
			return moment().format('YYYY-MM-DD')
		},

		totalUnread() {
			// Restituisce il valore dal backend invece di calcolarlo localmente
			return this.totalUnreadCount
		},

		groupedMessages() {
			const groups = {}
			this.conversationMessages.forEach(message => {
				// Usa sent_at se disponibile, altrimenti created_at
				// Questo gestisce correttamente sia messaggi normali che sincronizzati
				const messageDate = message.sent_at || message.created_at
				const date = new Date(messageDate).toDateString()
				if (!groups[date]) {
					groups[date] = []
				}
				groups[date].push(message)
			})
			return groups
		},

		canSendMessage() {
			if (!this.currentConversation) return false

			// Check if within 24h messaging window
			// After receiving an inbound message or sending a template,
			// you have 24 hours to send free messages
			if (!this.currentConversation.session_expires_at) return false

			const expiresAt = new Date(this.currentConversation.session_expires_at)
			return expiresAt > new Date()
		},

		isCurrentConversationVerified() {
			if (!this.currentConversation) return false

			// Check if contact is verified
			if (this.currentConversation.contact && this.currentConversation.contact.whatsapp_verified) {
				return true
			}

			// Check if company is verified
			if (this.currentConversation.company && this.currentConversation.company.whatsapp_verified) {
				return true
			}

			return false
		}
	},

	filters: {
		truncate(value, length = 50) {
			if (!value) return ''
			return value.length > length
				? value.substring(0, length) + '...'
				: value
		}
	},

	watch: {
		searchQuery() {
			// Debounce la ricerca di 500ms
			if (this.searchDebounceTimer) {
				clearTimeout(this.searchDebounceTimer)
			}

			this.searchDebounceTimer = setTimeout(() => {
				this.conversationsPage = 1
				this.conversations = []
				this.fetchConversations(1, false)
			}, 500)
		},

		statusFilter() {
			this.conversationsPage = 1
			this.conversations = []
			this.fetchConversations(1, false)
		}
	},

	mounted() {
		this.loadConversations().then(() => {
			// Check for conversation_id in query params
			this.checkAndOpenConversationFromQuery()
		})
		this.pusherConnect()

		// Aggiungi listener per tasto ESC
		window.addEventListener('keydown', this.handleEscKey)

		// Aggiungi listener per scroll su conversations list
		this.$nextTick(() => {
			const conversationsList = this.$refs.conversationsList
			if (conversationsList && conversationsList.$el) {
				conversationsList.$el.addEventListener('scroll', this.onConversationsScroll, { passive: true })
			}
		})
	},

	beforeDestroy() {
		this.pusherUnconnect()

		// Rimuovi listener per tasto ESC
		window.removeEventListener('keydown', this.handleEscKey)

		// Rimuovi listener per scroll
		const conversationsList = this.$refs.conversationsList
		if (conversationsList && conversationsList.$el) {
			conversationsList.$el.removeEventListener('scroll', this.onConversationsScroll)
		}
	},

	methods: {
		// === API METHODS (migrati da store) ===
		async fetchConversations(page = 1, append = false) {
			if (append) {
				this.loadingMoreConversations = true
			} else {
				this.conversationLoading = true
			}
			try {
				// Prepara filtri
				const filters = {
					order_by: 'last_message_at',
					order_dir: 'desc',
					per_page: 50,
					page: page
				}

				// Aggiungi filtro status
				if (this.statusFilter && this.statusFilter !== 'all') {
					filters.status = this.statusFilter
				}

				// Aggiungi filtro search
				if (this.searchQuery) {
					filters.search = this.searchQuery
				}

				const response = await this.axios.post('/whatsappconversations-filters', filters)
				const data = response.data.data || []

				if (append) {
					this.conversations = [...this.conversations, ...data]
				} else {
					this.conversations = data
				}

				// Aggiorna il totale delle conversazioni non lette dal backend
				if (response.data.total_unread !== undefined) {
					this.totalUnreadCount = response.data.total_unread
				}

				// Check if there are more conversations
				this.hasMoreConversations = response.data.next_page_url !== null ||
					(response.data.current_page && response.data.last_page && response.data.current_page < response.data.last_page)

				return response.data
			} catch (error) {
				console.error('Error fetching conversations:', error)
				throw error
			} finally {
				if (append) {
					this.loadingMoreConversations = false
				} else {
					this.conversationLoading = false
				}
			}
		},

		async fetchConversationMessages(conversationId, beforeMessageId = null) {
			try {
				const params = {}
				if (beforeMessageId) {
					params.before_message_id = beforeMessageId
				}
				const response = await this.axios.get(`/whatsappconversations-messages/${conversationId}`, { params })

				if (beforeMessageId) {
					// Caricamento messaggi più vecchi - prepend alla lista
					this.conversationMessages = [...response.data.data, ...this.conversationMessages]
				} else {
					// Caricamento iniziale
					this.conversationMessages = response.data.data || response.data
				}

				this.hasMoreMessages = response.data.has_more || false
				return response.data
			} catch (error) {
				console.error('Error fetching messages:', error)
				throw error
			}
		},

		async sendMessageAction(conversationId, message) {
			try {
				const payload = {
					conversation_id: conversationId,
					body: message
				}

				const response = await this.axios.post('/whatsappmessages-send', payload)

				// Aggiungere messaggio alla lista locale
				this.conversationMessages.push(response.data)

				// Aggiornare last_message della conversazione
				const conv = this.conversations.find(c => c.id === conversationId)
				if (conv) {
					conv.last_message_at = response.data.sent_at || response.data.created_at
					conv.last_message = response.data.body

					// Riordinare le conversazioni per mettere quella aggiornata in cima
					this.conversations.sort((a, b) => {
						const dateA = new Date(a.last_message_at || a.created_at)
						const dateB = new Date(b.last_message_at || b.created_at)
						return dateB - dateA
					})

					// Aggiornare anche filteredConversations
				}

				return response.data
			} catch (error) {
				console.error('Error sending message:', error)
				throw error
			}
		},

		async markConversationMessagesAsRead(conversationId, messageIds) {
			try {
				const response = await this.axios.post(`/whatsappconversations/${conversationId}/mark-read`)

				// Aggiornare stato locale
				messageIds.forEach(id => {
					const message = this.conversationMessages.find(m => m.id === id)
					if (message) {
						message.status = 'read'
						message.read_at = new Date()
					}
				})

				// Azzerare unread_count
				const conv = this.conversations.find(c => c.id === conversationId)
				if (conv) {
					conv.unread_count = 0
				}
				if (this.currentConversation?.id === conversationId) {
					this.currentConversation.unread_count = 0
				}

				// Aggiornare contatore globale non letti
				if (response.data.total_unread !== undefined) {
					this.totalUnreadCount = response.data.total_unread
				}
			} catch (error) {
				console.error('Error marking messages as read:', error)
				throw error
			}
		},

		// === WRAPPER METHODS ===
		async loadConversations() {
			try {
				this.conversationsPage = 1
				await this.fetchConversations(1, false)
				this.$store.commit('endProgress')
			} catch (error) {
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Impossibile caricare le conversazioni',
					value: true,
					color: 'error'
				})
				this.$store.commit('endProgress')
			}
		},

		async loadMessages() {
			if (!this.currentConversation) return

			try {
				await this.fetchConversationMessages(this.currentConversation.id)
				this.$nextTick(() => {
					this.scrollToBottom()
				})

				// Mark messages as read
				const unreadMessages = this.conversationMessages
					.filter(m => m.direction === 'inbound' && !m.read_at)
					.map(m => m.id)

				// Chiama l'endpoint se ci sono messaggi non letti O se la chat è stata segnata come da leggere (unread_count === -1)
				if (unreadMessages.length > 0 || this.currentConversation.unread_count === -1) {
					await this.markConversationMessagesAsRead(this.currentConversation.id, unreadMessages)
				}
			} catch (error) {
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Impossibile caricare i messaggi',
					value: true,
					color: 'error'
				})
			}
		},

		onScroll(event) {
			const container = event.target

			// Se lo scroll è vicino al top (entro 100px), carica messaggi più vecchi
			if (container.scrollTop < 100 && this.hasMoreMessages && !this.loadingOlderMessages) {
				this.loadOlderMessages()
			}

			// Traccia se siamo in fondo
			const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight
			const isAtBottom = scrollBottom < this.scrollBottomThreshold
			this.isAtBottom = isAtBottom

			// Mostra il pulsante scroll down quando si scrolla almeno 300px dal fondo
			// Una volta apparso, rimane visibile finché non torni in fondo (entro 100px)
			if (scrollBottom > 300) {
				this.showScrollDownButton = true
			} else if (isAtBottom) {
				this.showScrollDownButton = false
			}
		},

		async loadOlderMessages() {
			if (!this.hasMoreMessages || this.loadingOlderMessages || !this.currentConversation) return

			this.loadingOlderMessages = true
			const messagesContainer = this.$refs.messagesContainer
			const scrollHeightBefore = messagesContainer ? messagesContainer.scrollHeight : 0

			try {
				const oldestMessage = this.conversationMessages[0]
				if (oldestMessage) {
					await this.fetchConversationMessages(this.currentConversation.id, oldestMessage.id)

					// Mantieni la posizione dello scroll
					this.$nextTick(() => {
						if (messagesContainer) {
							const scrollHeightAfter = messagesContainer.scrollHeight
							messagesContainer.scrollTop = scrollHeightAfter - scrollHeightBefore
						}
					})
				}
			} catch (error) {
				console.error('Error loading older messages:', error)
			} finally {
				this.loadingOlderMessages = false
			}
		},

		async selectConversation(conversation) {
			// Leave previous conversation channel if connected
			if (this.$echo && this.currentConversation) {
				this.$echo.leave('whatsapp-conversation.' + this.$store.getters.uuid + '.' + this.currentConversation.id)
			}

			this.currentConversation = conversation
			await this.loadMessages()

			// Connect to new conversation channel for status updates
			if (this.$echo && this.currentConversation) {
				const channelName = 'whatsapp-conversation.' + this.$store.getters.uuid + '.' + this.currentConversation.id
				console.log('Connecting to conversation channel:', channelName)
				this.$echo.private(channelName)
				.listen('.WhatsAppMessageStatusUpdated', (e) => {
					console.log('WhatsApp message status updated via echo:', e)

					// Update message status in current conversation
					const message = this.conversationMessages.find(m => m.id === e.message_id)
					if (message) {
						message.status = e.status
						message.delivered_at = e.delivered_at
						message.read_at = e.read_at

						// Update media URLs if they changed (webhook updated them to proxy URLs)
						if (e.media_urls) {
							message.media_urls = e.media_urls
						}
					}
				})
			}
		},

		onConversationsScroll(event) {
			const container = event.target
			const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight

			// Se lo scroll è vicino al fondo (entro 100px), carica altre conversazioni
			if (scrollBottom < 100 && this.hasMoreConversations && !this.loadingMoreConversations) {
				this.loadMoreConversations()
			}
		},

		async loadMoreConversations() {
			if (!this.hasMoreConversations || this.loadingMoreConversations) return

			this.conversationsPage++
			try {
				await this.fetchConversations(this.conversationsPage, true)
			} catch (error) {
				console.error('Error loading more conversations:', error)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Impossibile caricare altre conversazioni',
					value: true,
					color: 'error'
				})
			}
		},

		async sendMessage() {
			if (!this.newMessage.trim() || !this.currentConversation) return

			try {
				await this.sendMessageAction(this.currentConversation.id, this.newMessage)

				this.newMessage = ''
				this.$nextTick(() => {
					this.scrollToBottom()
				})
			} catch (error) {
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: (error.response && error.response.data && error.response.data.message) || 'Impossibile inviare il messaggio',
					value: true,
					color: 'error'
				})
			}
		},

		async refreshMessages() {
			this.isRefreshing = true
			try {
				await this.loadMessages()
				this.$store.commit('setSnackbar', {
					timeout: 2000,
					text: 'Messaggi aggiornati',
					value: true,
					color: 'success'
				})
			} finally {
				this.isRefreshing = false
			}
		},

		async markConversationAsUnread() {
			if (!this.currentConversation) return

			try {
				await this.axios.put(`/whatsappconversations/${this.currentConversation.id}`, {
					unread_count: -1
				})

				// Aggiornare stato locale
				const conv = this.conversations.find(c => c.id === this.currentConversation.id)
				if (conv) {
					conv.unread_count = -1
				}
				if (this.currentConversation) {
					this.currentConversation.unread_count = -1
				}

				// Incrementare contatore globale non letti
				this.totalUnreadCount = this.totalUnreadCount + 1

				this.$store.commit('setSnackbar', {
					timeout: 2000,
					text: 'Chat segnata come da leggere',
					value: true,
					color: 'success'
				})
			} catch (error) {
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore durante l\'operazione',
					value: true,
					color: 'error'
				})
			}
		},

		async syncMessages() {
			if (!this.currentConversation) return

			this.syncLoading = true

			try {
				const payload = {}
				if (this.syncFromDate) {
					payload.from_date = this.syncFromDate
				}

				await this.axios.post(
					`whatsappconversations/${this.currentConversation.id}/sync-messages`,
					payload
				)

				this.syncDialog = false
				this.syncFromDate = null

				this.$store.commit('setSnackbar', {
					timeout: 4000,
					text: 'Sincronizzazione messaggi avviata. I messaggi verranno aggiornati a breve.',
					value: true,
					color: 'success'
				})

				// Ricarica messaggi dopo 2 secondi per vedere i nuovi messaggi sincronizzati
				setTimeout(async () => {
					await this.loadMessages()
				}, 2000)

			} catch (error) {
				console.error('Errore sincronizzazione messaggi:', error)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: error.response?.data?.message || 'Errore durante la sincronizzazione messaggi',
					value: true,
					color: 'error'
				})
			} finally {
				this.syncLoading = false
			}
		},

		onConversationCreated(conversation) {
			// Check if conversation already exists
			const existingIndex = this.conversations.findIndex(c => c.id === conversation.id)

			if (existingIndex === -1) {
				// Conversation doesn't exist, add it
				this.conversations.unshift(conversation)
			} else {
				// Conversation exists, update it and move to top
				this.conversations.splice(existingIndex, 1)
				this.conversations.unshift(conversation)
			}

			this.selectConversation(conversation)
			this.newConversationDialog = false
		},

		onTemplateSelected(payload) {
			// Handle template selection - payload = { template, variables }
			this.templateDialog = false
			// Send template message
			this.sendTemplateMessage(payload)
		},

		async sendTemplateMessage(payload) {
			if (!this.currentConversation) {
				this.$store.commit('setSnackbar', {
					text: 'Seleziona una conversazione prima di inviare un template',
					color: 'warning',
					value: true
				})
				return
			}

			try {
				const response = await this.axios.post('/whatsappmessages-send', {
					conversation_id: this.currentConversation.id,
					template_id: payload.template.value,
					template_variables: payload.variables
				})

				// Add message to local list
				this.conversationMessages.push(response.data)

				// Update conversation with new session window
				const conv = this.conversations.find(c => c.id === this.currentConversation.id)
				if (conv) {
					conv.last_message_at = response.data.sent_at || response.data.created_at
					conv.last_message = response.data.body
					conv.session_expires_at = response.data.session_expires_at || null
				}

				// Update current conversation session
				if (this.currentConversation) {
					this.currentConversation.session_expires_at = response.data.session_expires_at || null
				}

				this.$nextTick(() => {
					this.scrollToBottom()
				})

				this.$store.commit('setSnackbar', {
					text: 'Template inviato con successo',
					color: 'success',
					value: true,
					timeout: 3000
				})

			} catch (error) {
				console.error('Error sending template:', error)
				this.$store.commit('setSnackbar', {
					text: error.response?.data?.message || 'Errore durante l\'invio del template',
					color: 'error',
					value: true
				})
			}
		},

		scrollToBottom() {
			this.$nextTick(() => {
				if (this.$refs.messagesContainer) {
					const container = this.$refs.messagesContainer
					container.scrollTop = container.scrollHeight
				}
			})
		},

		pusherConnect() {
			console.log('pusherConnect called, $echo:', this.$echo)
			console.log('UUID:', this.$store.getters.uuid)
			if (this.$echo) {
				console.log('Connecting to channel: whatsapp.' + this.$store.getters.uuid)
				this.$echo.private('whatsapp.' + this.$store.getters.uuid)
				.listen('.WhatsAppMessageReceived', (e) => {
					console.log('WhatsApp message received via echo:', e)

					// Aggiornare lista conversazioni
					this.fetchConversations().then(() => {
						})

					// Se il messaggio è per la conversazione corrente, aggiungerlo
					if (this.currentConversation && e.message?.conversation_id === this.currentConversation.id) {
						// Aggiungere messaggio solo se non esiste già
						const exists = this.conversationMessages.find(m => m.id === e.message.id)
						if (!exists) {
							this.conversationMessages.push(e.message)
							this.$nextTick(() => this.scrollToBottom())
						}

						// Mark as read se la finestra è in focus
						if (document.hasFocus()) {
							this.markConversationMessagesAsRead(this.currentConversation.id, [e.message.id])
						}
					} else if (e.message) {
						// Incrementare unread count per altre conversazioni
						const conv = this.conversations.find(c => c.id === e.message.conversation_id)
						if (conv) {
							conv.unread_count = (conv.unread_count || 0) + 1
							conv.last_message_at = e.message.sent_at || e.message.created_at
							conv.last_message = e.message.body
						}
					}
				})
			} else {
				console.warn('$echo not available')
			}
		},

		pusherUnconnect() {
			if (this.$echo) {
				this.$echo.leave('whatsapp.' + this.$store.getters.uuid)
				// Leave conversation channel if connected
				if (this.currentConversation) {
					this.$echo.leave('whatsapp-conversation.' + this.$store.getters.uuid + '.' + this.currentConversation.id)
				}
			}
		},

		openMedia(url, type = 'image', message = null, index = null) {
			this.currentMediaUrl = url
			this.currentMediaType = type
			this.currentMediaMessage = message
			this.currentMediaIndex = index
			this.mediaDialog = true
		},

		getMediaType(message, index) {
			if (!message.media_content_types || !message.media_content_types[index]) {
				return 'unknown'
			}

			const contentType = message.media_content_types[index]

			if (contentType.startsWith('image/')) return 'image'
			if (contentType.startsWith('audio/')) return 'audio'
			if (contentType.startsWith('video/')) return 'video'
			if (contentType.includes('pdf') || contentType.includes('document')) return 'document'

			return 'unknown'
		},

		isImageMedia(message, index) {
			return this.getMediaType(message, index) === 'image'
		},

		isAudioMedia(message, index) {
			return this.getMediaType(message, index) === 'audio'
		},

		isVideoMedia(message, index) {
			return this.getMediaType(message, index) === 'video'
		},

		isDocumentMedia(message, index) {
			return this.getMediaType(message, index) === 'document'
		},

		// Il body arriva dal mittente esterno e finisce in v-html: va escapato
		// prima di trasformare gli URL in link (vedi utils/sanitizeHtml.js)
		formatMessageBody(body) {
			return escapeAndLinkify(body)
		},

		formatDate(date) {
			if (!date) return ''
			const d = new Date(date)
			const today = new Date()

			if (d.toDateString() === today.toDateString()) {
				return this.formatTime(date)
			}

			const yesterday = new Date(today)
			yesterday.setDate(yesterday.getDate() - 1)

			if (d.toDateString() === yesterday.toDateString()) {
				return 'Ieri'
			}

			return d.toLocaleDateString('it-IT', {
				day: '2-digit',
				month: '2-digit'
			})
		},

		formatTime(date) {
			if (!date) return ''
			return new Date(date).toLocaleTimeString('it-IT', {
				hour: '2-digit',
				minute: '2-digit'
			})
		},

		formatMessageDate(date) {
			const d = new Date(date)
			const today = new Date()

			if (d.toDateString() === today.toDateString()) {
				return 'Oggi'
			}

			const yesterday = new Date(today)
			yesterday.setDate(yesterday.getDate() - 1)

			if (d.toDateString() === yesterday.toDateString()) {
				return 'Ieri'
			}

			return d.toLocaleDateString('it-IT', {
				weekday: 'long',
				day: 'numeric',
				month: 'long',
				year: 'numeric'
			})
		},

		getLastMessageText(conversation) {
			if (!conversation.last_message) return ''

			// If last_message is a string, try to parse it as JSON
			if (typeof conversation.last_message === 'string') {
				try {
					const message = JSON.parse(conversation.last_message)
					return message.body || ''
				} catch (e) {
					return conversation.last_message
				}
			}

			// If it's an object, get the body
			if (typeof conversation.last_message === 'object') {
				return conversation.last_message.body || ''
			}

			return ''
		},

		getMediaUrl(url, message, index) {
			if (!url) return ''

			const baseUrl = this.$store.getters.baseUrl

			// 1. Attachment download URL (temporary before webhook updates)
			if (url.startsWith('/api/attachments/')) {
				const token = localStorage.getItem('user-token')
				return `${baseUrl}${url}?token=${token}`
			}

			// 2. Media via proxy Twilio: /whatsapp/twilio-proxy/MM123/ME456
			// Preferito per performance (CDN Twilio)
			if (url.startsWith('/whatsapp/twilio-proxy/')) {
				const token = localStorage.getItem('user-token')
				// Setup fallback URL for when Twilio media expires (13 months)
				const twilioUrl = `${baseUrl}/api${url}?token=${token}`

				// If we have message and index, we can setup fallback
				if (message && index !== undefined && message.id) {
					// Create an image/media element with error handler for fallback
					return {
						primary: twilioUrl,
						fallback: `${baseUrl}/api/whatsapp/media/${message.id}/${index}?token=${token}`
					}
				}

				return twilioUrl
			}

			// 3. Local storage URL for permanent backup
			if (url.startsWith('/api/whatsapp/media/')) {
				const token = localStorage.getItem('user-token')
				return `${baseUrl}${url}?token=${token}`
			}

			// 4. Legacy: /whatsapp/media/ → convert to proxy Twilio
			if (url.startsWith('/whatsapp/media/')) {
				const token = localStorage.getItem('user-token')
				const proxyUrl = url.replace('/whatsapp/media/', '/whatsapp/twilio-proxy/')
				return `${baseUrl}/api${proxyUrl}?token=${token}`
			}

			// 5. URL già completo (https://...)
			return url
		},

		// Helper method to handle media URL with fallback
		getMediaSrc(url, message, index) {
			const mediaUrl = this.getMediaUrl(url, message, index)

			// If it's an object with fallback, return primary URL
			// The component will need to handle error for fallback
			if (typeof mediaUrl === 'object' && mediaUrl.primary) {
				return mediaUrl.primary
			}

			return mediaUrl
		},

		getInitials(name) {
			if (!name) return '?'
			const parts = name.split(' ')
			if (parts.length >= 2) {
				return parts[0][0] + parts[parts.length - 1][0]
			}
			return name.substring(0, 2).toUpperCase()
		},

		getStatusColor(status) {
			const colors = {
				active: 'success',
				pending: 'warning',
				closed: 'grey'
			}
			return colors[status] || 'grey'
		},

		getStatusLabel(status) {
			const labels = {
				active: 'Attiva',
				pending: 'In attesa',
				closed: 'Chiusa'
			}
			return labels[status] || status
		},

		getMessageStatusIcon(status) {
			const icons = {
				queued: 'mdi-clock-outline',
				sent: 'mdi-check',
				delivered: 'mdi-check-all',
				read: 'mdi-check-all',
				failed: 'mdi-alert-circle'
			}
			return icons[status] || 'mdi-help-circle'
		},

		getMessageStatusColor(status) {
			const colors = {
				queued: 'grey',
				sent: 'grey',
				delivered: 'grey',
				read: 'blue',
				failed: 'red'
			}
			return colors[status] || 'grey'
		},

		openUploadDialog() {
			this.uploadDialog = true
		},

		handleFileSelect(event) {
			const file = event.target.files[0]
			if (file) {
				this.selectedFile = file

				// Create preview for images
				if (file.type.startsWith('image/')) {
					const reader = new FileReader()
					reader.onload = e => {
						this.filePreview = e.target.result
					}
					reader.readAsDataURL(file)
				} else {
					this.filePreview = null
				}
			}
		},

		async sendMediaFile() {
			if (!this.selectedFile || !this.currentConversation) return

			this.uploading = true

			try {
				const formData = new FormData()
				formData.append('file', this.selectedFile)
				formData.append('conversation_id', this.currentConversation.id)
				if (this.caption) {
					formData.append('caption', this.caption)
				}

				const response = await this.axios.post('/whatsappmessages-send-media', formData, {
					headers: {
						'Content-Type': 'multipart/form-data'
					}
				})

				// Add message to local list
				this.conversationMessages.push(response.data)

				// Update conversation
				const conv = this.conversations.find(c => c.id === this.currentConversation.id)
				if (conv) {
					conv.last_message_at = response.data.sent_at || response.data.created_at
					conv.last_message = this.caption || 'Media inviato'
				}

				// Close dialog and reset
				this.uploadDialog = false
				this.selectedFile = null
				this.filePreview = null
				this.caption = ''
				if (this.$refs.fileInput) {
					this.$refs.fileInput.value = ''
				}

				this.$nextTick(() => {
					this.scrollToBottom()
				})

				this.$store.commit('setSnackbar', {
					timeout: 3000,
					text: 'Media inviato con successo',
					value: true,
					color: 'success'
				})

			} catch (error) {
				console.error('Error sending media:', error)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: error.response?.data?.error || 'Errore durante l\'invio del media',
					value: true,
					color: 'error'
				})
			} finally {
				this.uploading = false
			}
		},

		cancelUpload() {
			this.uploadDialog = false
			this.selectedFile = null
			this.filePreview = null
			this.caption = ''
			if (this.$refs.fileInput) {
				this.$refs.fileInput.value = ''
			}
		},

		// === MESSAGE ACTIONS ===
		async copyMessage(message) {
			if (!message.body) return

			try {
				await navigator.clipboard.writeText(message.body)
				this.$store.commit('setSnackbar', {
					text: 'Messaggio copiato',
					value: true,
					color: 'success',
					timeout: 2000
				})
			} catch (e) {
				console.error('Copy failed:', e)
				this.$store.commit('setSnackbar', {
					text: 'Impossibile copiare il messaggio',
					value: true,
					color: 'error',
					timeout: 3000
				})
			}
		},

		showMessageInfo(message) {
			this.selectedMessage = message
			this.messageInfoDialog = true
		},

		truncateText(text, length) {
			if (!text) return ''
			if (text.length <= length) return text
			return text.substring(0, length) + '...'
		},

		scrollToMessage(messageId) {
			const messageRef = this.$refs[`message-${messageId}`]
			const container = this.$refs.messagesContainer

			if (messageRef && messageRef[0] && container) {
				const element = messageRef[0]
				const containerRect = container.getBoundingClientRect()
				const elementRect = element.getBoundingClientRect()

				// Calcola la posizione di scroll per centrare il messaggio
				const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - (containerRect.height / 2) + (elementRect.height / 2)

				// Scroll smooth dentro il container
				container.scrollTo({
					top: scrollTop,
					behavior: 'smooth'
				})

				// Evidenzia il messaggio per 5 secondi
				this.highlightedMessageId = messageId
				setTimeout(() => {
					this.highlightedMessageId = null
				}, 5000)
			} else {
				// Il messaggio potrebbe non essere caricato (troppo vecchio)
				this.$store.commit('setSnackbar', {
					text: 'Messaggio non disponibile nella chat corrente',
					value: true,
					color: 'info',
					timeout: 3000
				})
			}
		},

		onMediaLoaded() {
			// Quando un media finisce di caricare, se eravamo in fondo manteniamo la posizione
			if (this.isAtBottom) {
				this.$nextTick(() => {
					this.scrollToBottom()
				})
			}
		},

		handleMediaError(event, url, message, index) {
			console.log('Media load error, trying fallback', { url, messageId: message.id, index })

			// If we have local media paths, try to use them as fallback
			if (message.id && index !== undefined) {
				const token = localStorage.getItem('user-token')
				const baseUrl = this.$store.getters.baseUrl
				const fallbackUrl = `${baseUrl}/api/whatsapp/media/${message.id}/${index}?token=${token}`

				// Replace the src with fallback URL
				if (event && event.target) {
					event.target.src = fallbackUrl
				}
			}
		},

		formatDateTime(datetime) {
			if (!datetime) return ''
			const d = new Date(datetime)
			return d.toLocaleString('it-IT', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			})
		},

		onMessageMouseLeave(messageId) {
			// Non nascondere il button se il menu è aperto
			if (!this.openMenuId[messageId]) {
				this.hoveredMessageId = null
			}
		},

		async onConversationLinked() {
			// Ricarica la conversazione corrente per aggiornare i dati
			if (this.currentConversation) {
				try {
					const response = await this.axios.get(`/whatsappconversations/${this.currentConversation.id}`)
					const updatedConversation = response.data.data || response.data

					// Aggiorna la conversazione corrente
					this.currentConversation = updatedConversation

					// Aggiorna anche nella lista delle conversazioni
					const index = this.conversations.findIndex(c => c.id === updatedConversation.id)
					if (index !== -1) {
						this.$set(this.conversations, index, updatedConversation)
					}

					// Rifiltra le conversazioni
				} catch (error) {
					console.error('Errore aggiornamento conversazione:', error)
				}
			}
		},

		onConversationUpdated(updatedConversation) {
			// Aggiorna la conversazione corrente
			this.currentConversation = updatedConversation

			// Aggiorna nella lista delle conversazioni
			const index = this.conversations.findIndex(c => c.id === updatedConversation.id)
			if (index !== -1) {
				this.$set(this.conversations, index, updatedConversation)
			}
		},

		handleEscKey(event) {
			// Deseleziona la conversazione corrente quando viene premuto ESC
			if (event.key === 'Escape' && this.currentConversation) {
				// Chiudi eventuali dialog/drawer aperti prima di deselezionare
				this.conversationInfo = false
				this.templateDialog = false
				this.mediaDialog = false
				this.uploadDialog = false

				// Deseleziona la conversazione
				this.currentConversation = null
				this.conversationMessages = []
			}
		},

		onSelectEmoji(emoji) {
			// Ottieni riferimento all'input
			const input = this.$refs.messageInput.$refs.input

			if (!input) {
				// Fallback: aggiungi l'emoji alla fine
				this.newMessage += emoji.data
				return
			}

			// Ottieni la posizione del cursore
			const startPos = input.selectionStart
			const endPos = input.selectionEnd

			// Inserisci l'emoji nella posizione del cursore
			this.newMessage =
				this.newMessage.substring(0, startPos) +
				emoji.data +
				this.newMessage.substring(endPos)

			// Sposta il cursore dopo l'emoji inserita
			this.$nextTick(() => {
				const newCursorPos = startPos + emoji.data.length
				input.setSelectionRange(newCursorPos, newCursorPos)
				input.focus()
			})

			// Non chiudere il menu per permettere di aggiungere più emoji
			// Se vuoi che si chiuda dopo ogni selezione, decommentare:
			// this.emojiMenu = false
		},

		async checkAndOpenConversationFromQuery() {
			// Check if there's a conversation_id in the query params
			const conversationId = this.$route.query.conversation_id

			if (conversationId) {
				// Find the conversation in the loaded conversations
				const conversation = this.conversations.find(c => c.id === parseInt(conversationId))

				if (conversation) {
					// Select and open the conversation
					await this.selectConversation(conversation)

					// Wait for DOM update
					await this.$nextTick()

					// Scroll to bottom of messages
					this.scrollToBottom()
				}
			}
		}
	}
}
</script>

<style scoped>

	.unread-dot {
		width: 16px !important;
		height: 16px !important;
		min-width: 16px !important;
		border-radius: 50% !important;
		padding: 0 !important;
	}

	.whatsapp-chat-container {
		height: calc(100vh - 154px);
		overflow: hidden;
	}

	.conversations-sidebar {
		border-right: 1px solid rgba(0, 0, 0, 0.12);
		height: 100%;
	}

	.conversations-list {
		overflow-y: auto;
		max-height: calc(100vh - 280px);
		padding-top: 0 !important;
	}

	.conversation-item-wrapper {
		transition: background-color 0.2s ease;
	}

	.conversation-item {
		padding: 6px 14px !important;
		cursor: pointer;
		transition: background-color 0.2s ease;
	}

	.conversation-item:hover {
		background-color: rgba(0, 0, 0, 0.04) !important;
	}

	.conversation-item.v-list-item--active {
		background-color: rgba(25, 118, 210, 0.08) !important;
		border-left: 4px solid #1976d2;
	}

	.chat-area {
		height: 100%;
		display: flex;
		flex-direction: column;
	}

	.messages-container {
		flex: 1;
		overflow-y: auto;
		padding: 16px;
		background-image: url('/img/whatsapp_background_light.png');
		background-repeat: repeat;
		background-color: #e5ddd5;
	}

	.scroll-down-btn {
		position: fixed !important;
		bottom: 130px;
		right: 36px;
		z-index: 1000;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
		transition: right 0.3s ease;
	}

	/* Quando il pannello ConversationInfo è aperto, sposta il pulsante a sinistra */
	.scroll-down-btn--with-info {
		right: calc(25% + 36px);
	}

	/* Responsive adjustments */
	@media (max-width: 1264px) {
		.scroll-down-btn--with-info {
			right: calc(25% + 36px);
		}
	}

	.theme--dark .messages-container {
		background-image: url('/img/whatsapp_background_dark.png');
		background-repeat: repeat;
		background-color: #0d1418;
	}

	.message-bubble {
		max-width: 65%;
		word-wrap: break-word;
		border-radius: 8px !important;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
		transition: box-shadow 0.2s ease;
		position: relative;
	}

	/* Aumenta la dimensione del bubble quando contiene media per permettere immagini/video più grandi */
	.message-bubble:has(.media-image),
	.message-bubble:has(.media-video) {
		max-width: 425px !important;
	}

	.message-bubble:hover {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
	}

	.message-highlighted {
		animation: messageHighlight 5s ease-in-out !important;
		outline: 3px solid #FFD700 !important;
		outline-offset: 3px !important;
	}

	@keyframes messageHighlight {
		0% {
			box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
			outline-color: transparent;
		}
		3% {
			box-shadow: 0 0 25px 6px rgba(255, 215, 0, 0.9);
			outline-color: #FFD700;
		}
		97% {
			box-shadow: 0 0 25px 6px rgba(255, 215, 0, 0.9);
			outline-color: #FFD700;
		}
		100% {
			box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
			outline-color: transparent;
		}
	}

	.theme--dark .message-highlighted {
		animation: messageHighlightDark 5s ease-in-out !important;
	}

	@keyframes messageHighlightDark {
		0% {
			box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
			outline-color: transparent;
		}
		3% {
			box-shadow: 0 0 30px 8px rgba(255, 215, 0, 0.7);
			outline-color: #FFD700;
		}
		97% {
			box-shadow: 0 0 30px 8px rgba(255, 215, 0, 0.7);
			outline-color: #FFD700;
		}
		100% {
			box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
			outline-color: transparent;
		}
	}

	.message-sent {
		border-bottom-right-radius: 2px !important;
	}

	.message-sent::after {
		content: '';
		position: absolute;
		bottom: 0;
		right: -6px;
		width: 0;
		height: 0;
		border-left: 8px solid #DCF8C6;
		border-bottom: 10px solid transparent;
		border-top: 3px solid transparent;
	}

	.message-received {
		border-bottom-left-radius: 2px !important;
	}

	.message-received::before {
		content: '';
		position: absolute;
		bottom: 0;
		left: -6px;
		width: 0;
		height: 0;
		border-right: 8px solid white;
		border-bottom: 10px solid transparent;
		border-top: 3px solid transparent;
	}

	.message-body {
		font-size: 14px;
		line-height: 1.5;
		color: #303030;
		text-align: left;
	}

	.message-info {
		font-size: 11px;
		color: rgba(0, 0, 0, 0.45);
		display: flex;
		align-items: center;
		justify-content: flex-end;
		margin-top: 4px;
	}

	.message-time {
		font-size: 11px;
		color: rgba(0, 0, 0, 0.45);
	}

	.message-input-container {
		border-top: 1px solid rgba(0, 0, 0, 0.12);
		background: white;
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;
		color: #757575;
	}

	/* Rimuovi ripple effect e overlay dal menu emoji */
	.emoji-picker-card {
		background-color: inherit !important;
	}

	.emoji-picker-card * {
		pointer-events: auto !important;
	}

	.emoji-picker-card ::v-deep .v-ripple__container {
		display: none !important;
	}

	.emoji-picker-card::before,
	.emoji-picker-card::after {
		display: none !important;
	}

	.emoji-picker-card ::v-deep .v-card__overlay {
		display: none !important;
	}

	.emoji-help-text {
		background-color: white !important;
	}

	.theme--dark .emoji-help-text {
		background-color: #1e1e1e !important;
	}

	::v-deep .v-slide-group__content {
		-ms-flex-pack: center !important;
		justify-content: center !important;
	}

	.typing-indicator {
		display: inline-block;
	}

	.typing-indicator span {
		display: inline-block;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: #9e9e9e;
		margin: 0 2px;
		animation: typing 1.4s infinite;
	}

	.typing-indicator span:nth-child(2) {
		animation-delay: 0.2s;
	}

	.typing-indicator span:nth-child(3) {
		animation-delay: 0.4s;
	}

	@keyframes typing {
		0%, 60%, 100% {
			transform: translateY(0);
		}
		30% {
			transform: translateY(-10px);
		}
	}

	.date-separator {
		text-align: center;
		margin: 20px 0;
		position: sticky;
		top: 0;
		z-index: 10;
		background: transparent;
		padding: 10px 0;
	}

	.date-chip {
		font-size: 12px !important;
		font-weight: 500 !important;
		padding: 6px 12px !important;
		height: auto !important;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
		position: relative;
		z-index: 1;
		background: rgba(255, 255, 255, 0.95) !important;
	}

	.theme--dark .date-chip {
		background: rgba(13, 20, 24, 0.95) !important;
	}

	/* Media Styles */
	.media-image {
		border-radius: 8px !important;
		overflow: hidden;
		transition: all 0.3s ease;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
		width: 300px !important;
		height: 300px !important;
		max-width: 300px !important;
		max-height: 300px !important;
	}

	.media-image:hover {
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
		transform: scale(1.02);
	}

	.audio-wrapper {
		background: rgba(0, 0, 0, 0.03);
		border-radius: 20px;
		padding: 8px 12px;
		padding-right: 0px;
		display: flex;
		align-items: center;
		gap: 0px;
		max-width: 400px;
		min-width: 350px;
	}

	.audio-icon {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.audio-player {
		display: block;
		width: 100%;
		min-width: 200px;
		outline: none;
		height: 32px;
	}

	.audio-player::-webkit-media-controls-panel {
		background: transparent;
	}

	.media-video {
		border-radius: 8px !important;
		overflow: hidden;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
		max-width: 400px !important;
		max-height: 400px !important;
	}

	.document-button {
		border-radius: 8px !important;
		text-transform: none !important;
		font-weight: 500 !important;
		padding: 8px 16px !important;
		transition: all 0.2s ease !important;
	}

	.document-button:hover {
		background: rgba(0, 0, 0, 0.08) !important;
		transform: translateY(-1px);
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
	}

	/* Message Actions Menu */
	.message-bubble-with-menu {
		position: relative;
		overflow: visible !important;
	}

	.message-bubble-with-menu .v-card__text {
		position: relative;
		z-index: 1;
	}

	.message-menu-btn {
		position: absolute !important;
		top: 4px !important;
		right: 4px !important;
		z-index: 999 !important;
		background: rgba(0, 0, 0, 0.2) !important;
		transition: all 0.2s ease !important;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
	}

	.message-menu-btn .v-icon {
		color: rgba(0, 0, 0, 0.6) !important;
	}

	.message-menu-btn:hover {
		background: rgba(0, 0, 0, 0.3) !important;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3) !important;
	}

	.message-menu-btn:hover .v-icon {
		color: rgba(0, 0, 0, 0.8) !important;
	}

	.theme--dark .message-menu-btn,
	.theme--dark .message-bubble-with-menu .message-menu-btn,
	.theme--dark.v-application .message-menu-btn {
		background: #424242 !important;
		border: 1px solid #616161 !important;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5) !important;
	}

	.theme--dark .message-menu-btn .v-icon,
	.theme--dark .message-bubble-with-menu .message-menu-btn .v-icon,
	.theme--dark.v-application .message-menu-btn .v-icon {
		color: #e0e0e0 !important;
	}

	.theme--dark .message-menu-btn:hover,
	.theme--dark .message-bubble-with-menu .message-menu-btn:hover,
	.theme--dark.v-application .message-menu-btn:hover {
		background: #616161 !important;
		border-color: #757575 !important;
		box-shadow: 0 3px 6px rgba(0, 0, 0, 0.6) !important;
	}

	.theme--dark .message-menu-btn:hover .v-icon,
	.theme--dark .message-bubble-with-menu .message-menu-btn:hover .v-icon,
	.theme--dark.v-application .message-menu-btn:hover .v-icon {
		color: #ffffff !important;
	}

	/* Reply Context Styles */
	.reply-context {
		display: flex;
		gap: 8px;
		padding: 8px 10px;
		background: rgba(0, 0, 0, 0.05);
		border-radius: 6px;
		cursor: pointer;
		transition: background 0.2s ease;
	}

	.reply-context:hover {
		background: rgba(0, 0, 0, 0.08);
	}

	.reply-bar {
		width: 4px;
		background: #1976d2;
		border-radius: 2px;
		flex-shrink: 0;
	}

	.reply-content {
		flex: 1;
		min-width: 0;
	}

	.reply-author {
		font-size: 12px;
		font-weight: 600;
		color: #1976d2;
		margin-bottom: 2px;
	}

	.reply-text {
		font-size: 13px;
		color: rgba(0, 0, 0, 0.6);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	</style>

	<style scoped>

	.conversations-list {
		max-height: calc(100vh - 340px);
	}

	.messages-container {
		background-image: url('/img/whatsapp_background_light.png');
		background-repeat: repeat;
		max-height: calc(100vh - 250px);
	}

	.theme--dark .messages-container {
		background-image: url('/img/whatsapp_background_dark.png');
		background-repeat: repeat;
		background-color: #0d1418;
	}

	.message-bubble {
		border-radius: 12px;
		word-break: break-word;
	}

	.message-body {
		white-space: pre-wrap;
	}

	.message-wrapper {
		display: flex;
	}

	/* Override Vuetify margin for prepend buttons */
	::v-deep .v-text-field--outlined .v-input__prepend-outer {
		margin-top: 10px !important;
	}
</style>
